import { t } from '@app/i18n';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { identityAccessHref } from '@app/navigation';
import { Alert, Button, Card } from '@zarya/ui';
import type { AccessMap, Divergences } from './AccessMap.api';
import { DIVERGENCES_UNDER_LENS } from './accessMapVocab';
import { clockTime } from './freshness';
import s from './AccessMap.module.css';

/**
 * Card **Divergências** de T19 (FR-104, FR-105).
 *
 * As abas do catálogo são as três perguntas que ele não responde sozinho: o que
 * foi concedido e o gateway não conhece, o que o gateway conhece e ninguém
 * alcança, e o que não tem dono. A quarta, «exposto por mais de um caminho»,
 * saiu com a D2 do aim-cpj (08/10): o mesmo nome em dois servidores são duas
 * tools (decisão 13). Cada item leva para a tela onde se conserta.
 *
 * Desde o aim-6ph.18 o card não tem estado «indisponível»: as listas saem do
 * Postgres, na mesma leitura do grafo, e vêm sempre (protótipo
 * `prototipo-aim-6ph14-mapa-divergencias.md`, aprovado em 10/10/2026).
 */

type TabID = keyof Divergences;

/**
 * As abas, na ordem. Cada uma guarda a CHAVE do catálogo, não a frase: o `id` vem
 * do contrato (`Divergences`) e não se traduz; rótulo e explicação, sim, e são
 * resolvidos no desenho.
 */
const ABAS: { id: TabID; key: string }[] = [
  { id: 'granted_without_catalog', key: 'outOfCatalog' },
  { id: 'cataloged_without_reach', key: 'withoutReach' },
  { id: 'without_owner', key: 'withoutOwner' },
  // Fase 2 (aim-co1.3): as duas do recurso agente, lidas do produto e não do gateway — prancha `Mapa-de-acesso`.
  { id: 'agent_without_route', key: 'agentWithoutRoute' },
  { id: 'agent_without_trusted_target', key: 'agentWithoutTrustedTarget' },
];

const tabLabel = (key: string) => t(`accessMap.divergences.${key}`);
const tabHelp = (key: string) => t(`accessMap.divergences.${key}Help`);

interface TabItem {
  chave: string;
  titulo: string;
  detalhe?: string;
  rota: string;
  acao: string;
}

/**
 * Rota do item. Todo item tem o id local desde o aim-6ph.12, e o contrato o
 * obriga desde o aim-6ph.18 (D3): a identidade dona do contrato em «Fora do
 * catálogo», o resource do catálogo nas outras abas.
 */
function routeOf(base: string, parametro: string, id: string): string {
  return `${base}?${parametro}=${id}`;
}

/**
 * A `chave` traz **a aba e o índice**, não o nome do item. O nome se repete
 * dentro da aba (duas identidades concedidas na mesma tool) e entre abas (a
 * tool sem alcance costuma ser a mesma sem dono); com chave duplicada o React
 * colapsa os filhos antigos ao reconciliar, o fiber que sobrou nunca é apagado
 * e a troca de aba deixa um `<li>` da aba anterior no DOM (visto no E2E T386).
 */
function tabItems(tab: TabID, divergencias: Divergences): TabItem[] {
  switch (tab) {
    case 'granted_without_catalog':
      return divergencias.granted_without_catalog.map((item, index) => ({
        chave: `${tab}#${index}#${item.spiffe_id ?? item.item}`,
        titulo: item.item,
        detalhe: item.spiffe_id ?? undefined,
        // Quem concedeu foi o contrato: é no Access Control que se tira.
        rota: identityAccessHref(item.identity_id),
        acao: t('accessMap.divergences.openIdentityAccess'),
      }));
    case 'cataloged_without_reach':
      return divergencias.cataloged_without_reach.map((item, index) => ({
        chave: `${tab}#${index}#${item.resource_id}`,
        titulo: item.item,
        rota: routeOf('/resources', 'drawer', item.resource_id),
        acao: t('accessMap.divergences.openResource'),
      }));
    case 'without_owner':
      return divergencias.without_owner.map((item, index) => ({
        chave: `${tab}#${index}#${item.resource_id}`,
        titulo: item.item,
        detalhe: t('accessMap.divergences.noOwnerDetail'),
        rota: routeOf('/resources', 'drawer', item.resource_id),
        acao: t('accessMap.divergences.setOwner'),
      }));
    default:
      return divergencias[tab].map((item, index) => ({
        chave: `${tab}#${index}#${item.resource_id}`,
        titulo: item.item,
        rota: routeOf('/resources', 'drawer', item.resource_id),
        acao: t('accessMap.divergences.openAgent'),
      }));
  }
}

export interface DivergencesCardProps {
  mapa?: AccessMap;
  /** Repassada ao `Card`: dentro do card grande de T19 ele perde a moldura. */
  className?: string;
}

export function DivergencesCard({ mapa, className }: DivergencesCardProps) {
  const navigate = useNavigate();
  const [tab, setAba] = useState<TabID>('granted_without_catalog');

  const divergencias = mapa?.divergences ?? null;
  const itens = divergencias ? tabItems(tab, divergencias) : [];
  const account = (id: TabID) => (divergencias ? tabItems(id, divergencias).length : 0);
  const abaAtual = ABAS.find((item) => item.id === tab);

  return (
    <Card
      className={className}
      title={t('accessMap.divergences.title')}
      tip={t('accessMap.divergences.tip')}
    >
      {/* Sob a lente, as divergências continuam sendo as do mapa base: o
          catálogo e os contratos não conhecem claims. Sem este aviso o operador
          leria a lista como se ela também estivesse filtrada pelo perfil
          escolhido (§9). */}
      {mapa?.lens.active ? (
        <span data-testid="divergences-under-lens">
          <Alert tone="info" title={t('accessMap.divergences.ignoreLensTitle')}>
            {DIVERGENCES_UNDER_LENS()}
          </Alert>
        </span>
      ) : null}

      {divergencias ? (
        <>
          {/* A hora é a do cálculo do mapa (`computed_at`), a mesma do «calculado
              às» do topo. O gateway só entra no console pela importação, e a frase
              diz isso (aim-6ph.18). */}
          {mapa ? (
            <p className={s.origem} data-testid="divergencias-lidas-as">
              {t('accessMap.divergences.readAt', { time: clockTime(mapa.computed_at) })}
            </p>
          ) : null}
          <div className={s.abas} role="tablist" aria-label={t('accessMap.divergences.tablistLabel')}>
            {ABAS.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                data-testid={`aba-${item.id}`}
                aria-selected={tab === item.id}
                className={[s.aba, tab === item.id ? s.abaOn : ''].filter(Boolean).join(' ')}
                onClick={() => setAba(item.id)}
              >
                {t('accessMap.divergences.tabWithCount', {
                  label: tabLabel(item.key),
                  n: account(item.id),
                })}
              </button>
            ))}
          </div>

          {abaAtual ? <p className={s.origem}>{tabHelp(abaAtual.key)}</p> : null}

          {itens.length === 0 ? (
            <p className={s.vazioTexto}>{t('accessMap.divergences.none')}</p>
          ) : (
            // `key={aba}`: a lista inteira é outra a cada aba, e sem isso o
            // React reaproveita os `<li>` de uma aba na seguinte.
            <ul key={tab} className={s.divergencias} data-testid={`itens-${tab}`}>
              {itens.map((item) => (
                <li key={item.chave} className={s.divergencia} data-testid="divergencia">
                  <span className={s.divergenciaTexto}>
                    <span className={s.mono}>{item.titulo}</span>
                    {item.detalhe ? <span className={s.origem}>{item.detalhe}</span> : null}
                  </span>
                  <Button
                    variant="out"
                    size="sm"
                    data-rota={item.rota}
                    onClick={() => navigate(item.rota)}
                  >
                    {item.acao}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : null}
    </Card>
  );
}
